import { defineStore } from 'pinia'
import { RendezVousApi, Configuration } from '../api'
import type { AvocatDto, CabinetDto, CalendrierResponseDto, RdvPostDto, RdvDto, ClientDto } from '../api/models'

// Configuration de l'API
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.callaw.avoloi.com'
const apiConfig = new Configuration({
  basePath: API_BASE_URL,
})

// Types pour les données du formulaire
interface ClientRdv {
  prenom: string
  nom: string
  email: string
  telephone: string
  optin?: string
}

interface FormulaireRdv {
  motif: string
  idDomaine: number | undefined
  idSousDomaine: number | undefined
  codePostal: string | undefined
  idBar: number | undefined
  aideJuridictionnelle: boolean | undefined
  typeRdv: 'physique' | 'visio' | 'telephone' | ''
  dateChoisie: string | undefined
  heureChoisie: string | undefined
  creneauId: string | undefined
  description: string
  pieceJointe: File | undefined
  client: ClientRdv
}

interface SauvegardeTemporaire {
  formulaire: FormulaireRdv
  avocatCible: AvocatDto | undefined
  cabinetCible: CabinetDto | undefined
  etapeCourante: number
  etapesValidees: number[]
  timestamp: number
}

interface RendezVousState {
  formulaire: FormulaireRdv
  avocatCible: AvocatDto | undefined
  cabinetCible: CabinetDto | undefined
  creneauxDisponibles: CalendrierResponseDto[]
  etapeCourante: number
  etapesValidees: number[]
  loading: boolean
  error: string | undefined
  demandeEnvoyee: boolean
  reponseRdv: RdvDto | undefined
  sauvegardeTemporaire: SauvegardeTemporaire | undefined
  prescriber?: string
}

const emptyFormulaire = (): FormulaireRdv => ({
  motif: '',
  idDomaine: undefined,
  idSousDomaine: undefined,
  aideJuridictionnelle: undefined,
  typeRdv: '',
  dateChoisie: undefined,
  heureChoisie: undefined,
  creneauId: undefined,
  description: '',
  pieceJointe: undefined,
  codePostal: undefined,
  idBar: undefined,
  client: {
    prenom: '',
    nom: '',
    email: '',
    telephone: '',
    optin: 'non'
  }
})

export const useRendezVousStore = defineStore('rendez-vous', {
  state: (): RendezVousState => ({
    formulaire: emptyFormulaire(),
    avocatCible: undefined,
    cabinetCible: undefined,
    creneauxDisponibles: [],
    etapeCourante: 1,
    etapesValidees: [],
    loading: false,
    error: undefined,
    demandeEnvoyee: false,
    reponseRdv: undefined,
    sauvegardeTemporaire: undefined,
    prescriber: undefined
  }),

  getters: {
    etapeValide: (state): boolean => {
      switch (state.etapeCourante) {
        case 1: return state.formulaire.idDomaine !== undefined // Domaine
        case 2: return state.formulaire.description.trim().length >= 50 // Description
        case 3: return state.formulaire.client.prenom.trim().length > 0 && // Informations client
          state.formulaire.client.nom.trim().length > 0 &&
          state.formulaire.client.email.trim().length > 0 &&
          state.formulaire.client.telephone.trim().length > 0
        default: return false
      }
    },

    peutAvancer(): boolean {
      return this.etapeValide && this.etapeCourante < 3
    },

    peutReculer: (state): boolean => state.etapeCourante > 1,

    formulaireComplet(): boolean {
      // Si on est à l'étape 3, vérifier qu'elle est valide
      if (this.etapeCourante === 3) {
        return this.etapeValide
      }
      // Sinon, vérifier que toutes les étapes sont validées
      return this.etapesValidees.length === 3
    },

    donneesAPI: (state): RdvPostDto => {
      // Formater le numéro de téléphone au format international
      const formatPhoneNumber = (phone: string): string => {
        // Supprimer tous les caractères non numériques
        const numbers = phone.replace(/\D/g, '')
        // Si le numéro commence par 0, le remplacer par +33
        if (numbers.startsWith('0')) {
          return '+33' + numbers.substring(1)
        }
        // Si le numéro ne commence pas par +, ajouter +
        if (!phone.startsWith('+')) {
          return '+' + numbers
        }
        return phone
      }

      // Formater l'optin comme une date ISO
      const optinDate = new Date().toISOString()

      const clientAPI: ClientDto = {
        prenom: state.formulaire.client.prenom,
        nom: state.formulaire.client.nom,
        email: state.formulaire.client.email,
        telephone: formatPhoneNumber(state.formulaire.client.telephone),
        optin: optinDate
      }

      // Construire la date du RDV si dateChoisie et heureChoisie sont définies
      const rdvDate = state.formulaire.dateChoisie && state.formulaire.heureChoisie
        ? `${state.formulaire.dateChoisie}T${state.formulaire.heureChoisie}`
        : undefined

      // Déterminer le type de RDV
      const idTypeRdv = undefined


      // Déterminer le prescriber
      const idPrescriber = state.prescriber === 'call-a-lawyer' ? 52 : 1;

      return {
        motif: state.formulaire.motif || 'Consultation juridique',
        idDomaine: state.formulaire.idDomaine || 0,
        idSousDomaine: state.formulaire.idSousDomaine || 0,
        aideJuridictionnelle: state.formulaire.aideJuridictionnelle || false,
        idTypeRdv,
        rdvDate,
        description: state.formulaire.description,
        client: clientAPI,
        uuidAvocat: state.avocatCible?.uuid || undefined,
        uuidOffice: state.cabinetCible?.uuid || undefined,
        codePostal: state.formulaire.codePostal || '',
        idBar: state.formulaire.idBar || 0,
        idPrescriber: idPrescriber
      }
    }
  },

  actions: {
    initNouveauRdv(
      avocat: AvocatDto | undefined = undefined,
      cabinet: CabinetDto | undefined = undefined,
      options?: {
        idDomaine?: number,
        idSousDomaine?: number,
        codePostal?: string,
        idBar?: number
      }
    ): void {
      this.resetFormulaire()
      this.avocatCible = avocat
      this.cabinetCible = cabinet
      this.etapeCourante = 1

      // Pré-remplir les données si fournies
      if (options?.idDomaine) {
        this.formulaire.idDomaine = options.idDomaine
      }
      if (options?.idSousDomaine) {
        this.formulaire.idSousDomaine = options.idSousDomaine
      }
      if (options?.codePostal) {
        this.formulaire.codePostal = options.codePostal
      }
      if (options?.idBar) {
        this.formulaire.idBar = options.idBar
      }
    },

    resetFormulaire(): void {
      this.formulaire = emptyFormulaire()
      this.etapesValidees = []
      this.creneauxDisponibles = []
      this.demandeEnvoyee = false
      this.reponseRdv = undefined
      this.error = undefined
    },

    updateEtape(etape: number, donnees: any): void {
      switch (etape) {
        case 1: // Domaine
          this.formulaire.idDomaine = donnees.idDomaine
          this.formulaire.idSousDomaine = donnees.idSousDomaine
          break
        case 2: // Description
          this.formulaire.description = donnees.description
          if (donnees.pieceJointe) this.formulaire.pieceJointe = donnees.pieceJointe
          break
        case 3: // Informations client
          this.formulaire.client = { ...this.formulaire.client, ...donnees }
          break
      }
      this.sauvegarderTemporairement()
    },

    etapeSuivante(): void {
      if (this.etapeValide && this.etapeCourante < 3) {
        if (!this.etapesValidees.includes(this.etapeCourante)) {
          this.etapesValidees.push(this.etapeCourante)
        }
        this.etapeCourante++
      }
    },

    etapePrecedente(): void {
      if (this.etapeCourante > 1) {
        this.etapeCourante--
      }
    },

    allerAEtape(numeroEtape: number): void {
      if (numeroEtape >= 1 && numeroEtape <= 3) {
        this.etapeCourante = numeroEtape
      }
    },

    async envoyerDemandeRdv(): Promise<RdvDto | undefined> {
      if (!this.formulaireComplet) {
        this.error = 'Veuillez compléter toutes les étapes du formulaire'
        throw new Error('Formulaire incomplet')
      }
      this.loading = true
      this.error = undefined
      try {
        const api = new RendezVousApi(apiConfig)
        const donneesAPI = this.donneesAPI
        const response = await api.rendezVousControllerCreateRDV(donneesAPI)
        this.reponseRdv = response.data
        this.demandeEnvoyee = true
        this.supprimerSauvegardeTemporaire()
        return response.data
      } catch (error) {
        console.error('Erreur lors de l\'envoi du RDV:', error)
        this.error = 'Erreur lors de l\'envoi de la demande. Veuillez réessayer.'
        throw error
      } finally {
        this.loading = false
      }
    },

    sauvegarderTemporairement(): boolean {
      try {

        const donnees: SauvegardeTemporaire = {
          formulaire: this.formulaire,
          avocatCible: undefined,
          cabinetCible: undefined,
          etapeCourante: this.etapeCourante,
          etapesValidees: this.etapesValidees,
          timestamp: Date.now()
        }

        // Encoder en base64 URL-safe
        const donneesString = JSON.stringify(donnees)
        localStorage.setItem('rdv_temporaire', donneesString)
        this.sauvegardeTemporaire = donnees
        return true
      } catch (error) {
        console.error('Erreur lors de la sauvegarde temporaire:', error)
        return false
      }
    },

    restaurerSauvegardeTemporaire(): boolean {
      try {
        const donneesString = localStorage.getItem('rdv_temporaire')
        if (donneesString) {
          const donnees: SauvegardeTemporaire | undefined = JSON.parse(donneesString)
          if (donnees) {
            const maintenant = Date.now()
            const delai = 24 * 60 * 60 * 1000 // 24 heures
            if (maintenant - donnees.timestamp < delai) {
              //  Restaurer les données du formulaire
              this.formulaire = donnees.formulaire
              this.etapeCourante = donnees.etapeCourante
              this.etapesValidees = donnees.etapesValidees
              this.sauvegardeTemporaire = donnees

              // Sauvegarder à nouveau pour mettre à jour le timestamp
              return this.sauvegarderTemporairement()
            } else {
              // Supprimer la sauvegarde si elle est trop ancienne
              return this.supprimerSauvegardeTemporaire()
            }
          }
          return false
        }
        return false
      } catch (error) {
        console.error('Erreur lors de la restauration:', error)
        return false
      }
    },

    supprimerSauvegardeTemporaire(): boolean {
      try {
        localStorage.removeItem('rdv_temporaire')
        this.sauvegardeTemporaire = undefined
        return true
      } catch (error) {
        console.error('Erreur lors de la suppression de la sauvegarde:', error)
        return false
      }
    }
  },
}) 